import { useState, useEffect, useMemo, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { App, Tabs } from 'antd'
import { SkeletonSurveyInsights } from '../../../features/skeleton'
import { InterviewService } from '../services'
import { InterviewSynthesisTab } from '../components/insights/InterviewSynthesisTab'
import { InterviewAllResponsesTab } from '../components/insights/InterviewAllResponsesTab'
import { InterviewIndividualResponsesView } from '../components/insights/InterviewIndividualResponsesView'
import { ValidationScopeTab } from '../../../components/common/ValidationScopeTab'
import { InsightsContainer, Divider } from '../../survey/pages/SurveyInsightsPageStyles'
import { PRINT_STYLES } from '../../../styles/pageHeaderStyles'
import type { InterviewNotesResponse } from '@bandos/shared'
import styled from 'styled-components'
import { SPACING } from '../../../styles/theme'
import { reconcileInterviewQuestions } from './interviewInsightsUtils'
import { InterviewInsightsHeader } from './InterviewInsightsHeader'
import { InterviewInsightsMeta } from './InterviewInsightsMeta'
import { useInterviewInsightsActions } from '../hooks/useInterviewInsightsActions'

const ErrorContainer = styled.div`
  text-align: center;
  padding: ${SPACING.xxxl} ${SPACING.xl};
  color: var(--color-text-secondary);
`

export function InterviewInsightsPage() {
  const { nodeId } = useParams<{ nodeId: string }>()
  const navigate = useNavigate()
  const { notification } = App.useApp()

  const [data, setData] = useState<InterviewNotesResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isPrinting, setIsPrinting] = useState(false)

  const fetchNotes = useCallback(async () => {
    if (!nodeId) return
    setLoading(true)
    const result = await InterviewService.getNotes(nodeId)
    setLoading(false)

    if (result.error || !result.data) {
      const status = (result.error as { status?: number } | null)?.status
      if (status === 401 || status === 403) {
        notification.error({ title: 'Access denied' })
        void navigate('/')
        return
      }
      setError('Interview not found or you do not have access.')
      return
    }

    setData(result.data)
  }, [nodeId, navigate, notification])

  // Detect print mode
  useEffect(() => {
    const mq = window.matchMedia('print')
    const handler = (e: MediaQueryListEvent) => setIsPrinting(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  // Load notes
  useEffect(() => {
    void fetchNotes()
  }, [fetchNotes])

  const { handleSimulate, handleDelete, simulating } = useInterviewInsightsActions({
    nodeId,
    mindmapId: data?.mindmapId,
    parentLabel: data?.parentLabel,
    onNotesChanged: fetchNotes,
  })

  const reconciledQuestions = useMemo(
    () => reconcileInterviewQuestions(data?.questions ?? [], data?.notes ?? []),
    [data]
  )

  if (loading) {
    return <SkeletonSurveyInsights />
  }

  if (error || !data) {
    return (
      <InsightsContainer>
        <ErrorContainer>{error ?? 'Something went wrong.'}</ErrorContainer>
      </InsightsContainer>
    )
  }

  const { parentLabel, questions, notes } = data

  const hasMismatch = notes.some(
    (r) => r.questionsSnapshot === null && r.answers.length !== questions.length
  )

  const tabItems = [
    {
      key: 'synthesis',
      label: 'Findings',
      children: (
        <InterviewSynthesisTab
          notes={notes}
          questions={questions}
          nodeId={nodeId ?? ''}
          mindmapId={data.mindmapId}
          onSimulate={() => void handleSimulate()}
          simulating={simulating}
        />
      ),
    },
    {
      key: 'raw',
      label: 'All responses',
      children: (
        <InterviewAllResponsesTab
          reconciledQuestions={reconciledQuestions}
          noteCount={notes.length}
          hasMismatch={hasMismatch}
          isPrinting={isPrinting}
        />
      ),
    },
    {
      key: 'individual',
      label: 'Individual responses',
      children: <InterviewIndividualResponsesView notes={notes} questions={questions} />,
    },
    {
      key: 'scope',
      label: 'Mindmap nodes',
      children: (
        <ValidationScopeTab
          questions={questions}
          mindmapId={data.mindmapId}
          validationKind="interview"
        />
      ),
    },
  ]

  return (
    <InsightsContainer>
      <style>{PRINT_STYLES}</style>

      <InterviewInsightsHeader
        parentLabel={parentLabel}
        questionCount={questions.length}
        mindmapId={data.mindmapId}
        nodeId={nodeId ?? ''}
        simulating={simulating}
        onSimulate={() => void handleSimulate()}
        onDelete={handleDelete}
      />

      <InterviewInsightsMeta createdAt={data.createdAt} notes={notes} />

      <Divider />

      <Tabs items={tabItems} defaultActiveKey="synthesis" />
    </InsightsContainer>
  )
}
